import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
    registerDmsTableBlot,
    attachDmsTablePaste,
    withDmsTableFormat,
} from "../utils/dmsQuillTable";

/**
 * Round DMS-IssueRound-1 (2026) — Issue 2.
 *
 * The one rich-text editor every DMS field goes through (ECN Description of
 * Change / Reasons for Change / Instructions; SOP/WI/FCD/LIT Purpose, Scope,
 * Responsibilities, Definitions, Procedure / Work Instruction / Form / Notes).
 *
 * It is a thin wrapper over the react-quill editor that was already in use —
 * same theme, same toolbar (`modules`), same `formats` whitelist — plus:
 *   • the `dmsTable` block embed registered on react-quill's Quill instance
 *     (see utils/dmsQuillTable.js), whitelisted in `formats`;
 *   • Excel / spreadsheet paste interception: a copied range becomes a table,
 *     a single copied cell stays plain text, prose stays prose.
 *
 * Only the DocumentManagementSystem pages import this component; the Forms
 * module's FormEditor keeps its own untouched ReactQuill.
 *
 * Props are passed straight through to ReactQuill (value, onChange, modules,
 * formats, style, readOnly, placeholder …).
 */
registerDmsTableBlot();

const DmsRichTextEditor = forwardRef(function DmsRichTextEditor(
    { formats, onChange, ...rest },
    ref
) {
    const quillRef = useRef(null);
    useImperativeHandle(ref, () => quillRef.current, []);

    const mergedFormats = useMemo(() => withDmsTableFormat(formats), [formats]);

    useEffect(() => {
        const editor = quillRef.current && typeof quillRef.current.getEditor === "function"
            ? quillRef.current.getEditor()
            : null;
        if (!editor) return undefined;
        return attachDmsTablePaste(editor);
    }, []);

    return (
        <ReactQuill
            ref={quillRef}
            theme="snow"
            formats={mergedFormats}
            onChange={onChange}
            {...rest}
        />
    );
});

export default DmsRichTextEditor;
